import {EventEmitter, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, map, shareReplay, take} from 'rxjs/operators';
import {Observable, of, tap, throwError} from 'rxjs';

import {environment} from '@env/environment';
import {CreateClientModel} from '@models/create-client.model';
import {IClient, TAccountingPlatform} from '@shared/interface';
import {IClientAutoStatements, TAutoStatementsSetting} from '@models/auto-statements.model';
import {TIntegration} from '@shared/interface';
import {cache} from '@common/cache-provider';

/**
 * client management service
 */
@Injectable({
  providedIn: 'root'
})
export class ClientManagementService {
  clientsCache$: Observable<IClient[]>;
  onlyClientsCache$: Observable<IClient[]>;
  cache: Record<TIntegration, IClient[]> = {} as Record<TIntegration, IClient[]>;
  cacheWarmedUp: EventEmitter<void> = new EventEmitter<void>();
  /**
   * synchronous snapshot of the last resolved default client list.
   * Populated by {@link get} so consumers that cannot await (e.g. route guards)
   * can read the client list without subscribing.
   */
  clientsSnapshot: IClient[] = null;

  constructor(
    private http: HttpClient,
  ) {
  }

  /**
   * Returns list of clients
   */
  get(integration?: TIntegration | TAccountingPlatform): Observable<IClient[]> {
    if (integration && integration !== 'manual') {
      return this.getByIntegration(integration);
    }
    if (this.clientsCache$) {
      return this.clientsCache$;
    }
    this.clientsCache$ = this.http.get<{ clients: IClient[] }>(environment.API_URL + '/client/list').pipe(
      catchError(() => of({clients: [] as IClient[]})),
      map((data) => data.clients),
      tap((clients) => this.clientsSnapshot = clients),
      shareReplay(1)
    );
    return this.clientsCache$;
  }

  /**
   * Look up a single client by id out of the (cached) client list.
   * @param clientId - the client's id
   */
  getClientById(clientId: number): Observable<IClient | undefined> {
    return this.get().pipe(
      take(1),
      map((clients) => (clients || []).find((client) => client.id === clientId)),
    );
  }

  // idk why but endpoint /client/list returns customer as well, and customer does not have a client record
  getOnlyClients(integration?: TIntegration): Observable<IClient[]> {
    if (integration) {
      return this.getByIntegration(integration);
    }
    if (this.onlyClientsCache$) {
      return this.onlyClientsCache$;
    }
    this.onlyClientsCache$ = this.http.get<{ clients: IClient[] }>(environment.API_URL + '/clients').pipe(
      catchError(() => of({clients: [] as IClient[]})),
      map((data) => data.clients),
      shareReplay(1)
    );
    return this.onlyClientsCache$;
  }

  getByIntegration(integration: TIntegration | TAccountingPlatform): Observable<IClient[]> {
    if (cache.clients[integration]) {
      return of(cache.clients[integration]);
    }
    if (integration === 'manual') {
      return this.get();
    }
    return this.http.get<IClient[]>(environment.API_URL + `/client/list/${integration}`)
    .pipe(
      tap((data) => {
        cache.clients[integration] = data;
        // todo remove, backwards compatibility
        this.cache[integration] = data;
        this.cacheWarmedUp.emit();
      }),
    );
  }

  customerCopy(): Observable<IClient> {
    this.invalidateCache();
    return this.http.post<IClient>(environment.API_URL + '/client/customer-copy', {});
  }

  /**
   * get client
   * @param username -clients username
   */
  getClient(username): Observable<IClient> {
    return this.http.get<IClient>(environment.API_URL + `/client/details?username=${username}`);
  }

  /**
   * add client
   * @param data - obj clients data
   */
  addClient(data: CreateClientModel): Observable<IClient> {
    this.invalidateCache();
    return this.http.post<IClient>(environment.API_URL + '/client/add', data);
  }

  /**
   * delete client
   * @param username - username
   */
  deleteClient(username) {
    this.invalidateCache();
    return this.http.delete(environment.API_URL + `/client/delete?username=${encodeURIComponent(username)}`)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * update client
   * @param data - clients data
   */
  updateClient(data: IClient) {
    this.invalidateCache();
    return this.http.put(environment.API_URL + '/client/update', data)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * move a client's login to another email address, which also becomes its primary email (card #1479)
   * @param username - the client's current login
   * @param newEmail - the new login
   */
  changeLogin(username: string, newEmail: string): Observable<{ username: string }> {
    this.invalidateCache();
    return this.http.put<{ username: string }>(environment.API_URL + '/client/login', {username, newEmail});
  }

  /**
   *  send invitation email for client
   * @param data - clients username
   */
  invitationEmail(data) {
    return this.http.post(environment.API_URL + '/client/invitation/email', data)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  invitationSubscription(data) {
    return this.http.post(environment.API_URL + '/client/subscription/invitation', data)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * generate invitation link for client
   * @param data - clients username
   */
  invitationLink(data) {
    return this.http.post(environment.API_URL + '/client/invitation/link', data)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * synchronize banks account
   * param username
   */
  synchronize(username) {
    return this.http.put(environment.API_URL + '/accounts/banks/synchronize', {username})
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  enableSms(username) {
    this.invalidateCache();
    return this.http.put(environment.API_URL + '/client/enableSms', {username})
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  disableSms(username) {
    this.invalidateCache();
    return this.http.put(environment.API_URL + '/client/disableSms', {username})
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getWidgetInfo(username): Observable<any> {
    return this.http.get(`${environment.API_URL}/widget-info/${username}`)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  updateWidgetInfo(updateRequest): Observable<any> {
    return this.http.put(`${environment.API_URL}/widget-info`, updateRequest)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getAutoStatements(clientId: number): Observable<IClientAutoStatements> {
    return this.http.get<IClientAutoStatements>(`${environment.API_URL}/client/${clientId}/automatic-statements`);
  }

  /**
   * a client's own statement auto-pull choice; 422 for the firm's own record, 403 in a client's login
   * @param clientId - the client
   * @param enabled - on, off, or null to follow the firm
   */
  setAutoStatements(clientId: number, enabled: TAutoStatementsSetting): Observable<IClientAutoStatements> {
    return this.http.put<IClientAutoStatements>(`${environment.API_URL}/client/${clientId}/automatic-statements`, {enabled});
  }

  getInvitationEmail(username: string): Observable<any> {
    return this.http.get<any>(`${environment.API_URL}/client/invitation-email/${username}`);
  }

  /**
   * Invalidates all client caches
   * Should be called when client data is modified (add, update, delete, etc.)
   */
  private invalidateCache(): void {
    this.clientsCache$ = null;
    this.onlyClientsCache$ = null;
    this.clientsSnapshot = null;
    // Clear integration-specific caches
    Object.keys(cache.clients).forEach((integration) => {
      cache.resetClientsByIntegration(integration as TIntegration);
    });
  }
}
